import dayjs from 'dayjs'
import { ReactElement, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Skeleton, Box, Typography } from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchScheduledAssignment,
  fetchLinkedServices,
  fetchCourses,
  fetchTrainingSeries,
  selectLinkedServicesStatus,
  selectActiveScheduledAssignment,
  selectActiveScheduledAssignmentStatus,
  selectCourses,
  selectCoursesStatus,
  selectTrainingSeries,
  selectTrainingSeriesStatus,
  fetchLearningPaths,
  fetchFoamfratLearningPaths,
  selectLearningPathsByLinkedService,
  selectLearningPathsStatus,
  selectFoamfratLearningPathsStatus,
  fetchLightweightUsersV4,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  fetchGroups,
  selectGroupsWithDefaults,
  selectGroupsStatus,
  updateScheduledAssignment,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import WizardNavigation from '../assignments/components/form-wizard-navigation'
import UserSelectionStep from '../assignments/components/form-user-selection-step'
import ConfirmationModal from '../components/modals/confirmation-modal'
import SummaryModal from '../assignments/components/modals/summary-modal'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import ScheduledAssignmentDetailsCoursesStep from './components/form-details-courses-step'
import ScheduledAssignmentReviewStep from './components/form-review-step'
import { scheduledAssignmentSchema } from './validation'
import type { ScheduledAssignmentFormData, WizardStep } from './types'

export default function ScheduledAssignmentEdit(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { scheduledAssignmentID } = useParams()

  let [step, setStep] = useState<WizardStep>('details-courses')
  let [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  let [isSubmitting, setIsSubmitting] = useState(false)
  let { showSuccess, NotificationComponent } = useNotifications()
  let [summaryModalOpen, setSummaryModalOpen] = useState(false)
  let [summaryModalType, setSummaryModalType] = useState<'users' | 'courses'>(
    'users',
  )
  let [isFormInitialized, setIsFormInitialized] = useState(false)

  let scheduledAssignment = useAppSelector(selectActiveScheduledAssignment)
  let scheduledAssignmentStatus = useAppSelector(
    selectActiveScheduledAssignmentStatus,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let trainingSeries = useAppSelector(selectTrainingSeries)
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let learningPaths = useAppSelector(
    selectLearningPathsByLinkedService(
      scheduledAssignment?.linkedServiceID || '',
    ),
  )
  let learningPathsStatus = useAppSelector(selectLearningPathsStatus)
  let foamfratLearningPathsStatus = useAppSelector(
    selectFoamfratLearningPathsStatus,
  )

  let linkedServiceName = useLinkedServiceName(
    scheduledAssignment?.linkedServiceID,
  )

  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(
      scheduledAssignment?.linkedServiceID || '',
    ),
  )
  let lightweightUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(
      scheduledAssignment?.linkedServiceID || '',
    ),
  )

  let groups = useAppSelector(
    selectGroupsWithDefaults(scheduledAssignment?.linkedServiceID || ''),
  )
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let form = useForm({
    defaultValues: {
      name: '',
      linkedServiceID: '',
      goLiveDate: '',
      dueDate: '',
      userIDs: [] as string[],
      courseIDs: [] as string[],
      hasRecurrence: false,
      recurrence: undefined,
    } as ScheduledAssignmentFormData,
    validators: {
      onChange: scheduledAssignmentSchema,
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)

      let dueDaysAfterGoLive = dayjs(value.dueDate).diff(
        dayjs(value.goLiveDate),
        'day',
      )

      let recurrence =
        value.hasRecurrence && value.recurrence
          ? {
              ...value.recurrence,
              recurrenceEndDate:
                value.recurrence.recurrenceEndDate || undefined,
            }
          : undefined

      let payload = {
        id: scheduledAssignmentID!,
        linkedServiceID: value.linkedServiceID,
        name: value.name,
        goLiveDate: value.goLiveDate,
        dueDaysAfterGoLive,
        userIDs: value.userIDs,
        courseIDs: value.courseIDs,
        status: scheduledAssignment?.status || 'Pending',
        recurrence,
      }

      dispatch(updateScheduledAssignment(payload))

      showSuccess('Scheduled assignment updated successfully')
      setIsSubmitting(false)
      navigate('/manage/scheduled-assignments')
    },
  })

  useEffect(() => {
    if (scheduledAssignmentID) {
      dispatch(fetchScheduledAssignment(scheduledAssignmentID))
    }
  }, [dispatch, scheduledAssignmentID])

  // Reset form initialization flag when scheduled assignment ID changes
  useEffect(() => {
    setIsFormInitialized(false)
  }, [scheduledAssignmentID])

  useEffect(() => {
    if (
      scheduledAssignment &&
      scheduledAssignment.id === scheduledAssignmentID &&
      scheduledAssignmentStatus === 'fulfilled' &&
      !isFormInitialized
    ) {
      let calculatedDueDate = dayjs(scheduledAssignment.goLiveDate)
        .add(scheduledAssignment.dueDaysAfterGoLive, 'day')
        .toISOString()

      form.setFieldValue('name', scheduledAssignment.name || '')
      form.setFieldValue('linkedServiceID', scheduledAssignment.linkedServiceID)
      form.setFieldValue('goLiveDate', scheduledAssignment.goLiveDate)
      form.setFieldValue('dueDate', calculatedDueDate)
      form.setFieldValue('userIDs', scheduledAssignment.userIDs)
      form.setFieldValue('courseIDs', scheduledAssignment.courseIDs)
      form.setFieldValue('hasRecurrence', !!scheduledAssignment.recurrence)
      form.setFieldValue('recurrence', scheduledAssignment.recurrence)
      setIsFormInitialized(true)
    }
  }, [
    scheduledAssignment,
    scheduledAssignmentStatus,
    isFormInitialized,
    scheduledAssignmentID,
  ])

  let handleCancel = () => {
    setCancelDialogOpen(true)
  }

  let handleCancelConfirm = () => {
    navigate('/manage/scheduled-assignments')
  }

  let handleStepChange = (newStep: WizardStep) => {
    setStep(newStep)
  }

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(trainingSeriesStatus, fetchTrainingSeries)
  useAutoFetch(groupsStatus, fetchGroups)
  useAutoFetch(foamfratLearningPathsStatus, fetchFoamfratLearningPaths)

  useEffect(() => {
    if (
      learningPathsStatus === 'uninitialized' &&
      scheduledAssignment?.linkedServiceID &&
      scheduledAssignment.linkedServiceID !== 'unselected'
    ) {
      dispatch(fetchLearningPaths(scheduledAssignment.linkedServiceID))
    }
  }, [learningPathsStatus, scheduledAssignment?.linkedServiceID, dispatch])

  useEffect(() => {
    if (
      lightweightUsersStatus === 'uninitialized' &&
      scheduledAssignment?.linkedServiceID &&
      scheduledAssignment.linkedServiceID !== 'unselected'
    ) {
      dispatch(fetchLightweightUsersV4(scheduledAssignment.linkedServiceID))
    }
  }, [lightweightUsersStatus, scheduledAssignment?.linkedServiceID, dispatch])

  if (scheduledAssignmentStatus === 'pending' || !isFormInitialized) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={400} />
      </Container>
    )
  }

  if (scheduledAssignmentStatus === 'rejected' || !scheduledAssignment) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Scheduled Assignment Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The scheduled assignment you're looking for doesn't exist or has
            been deleted.
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {NotificationComponent}
      <PageTitleWithLinkedService
        pageTitle="Edit Scheduled Assignment"
        linkedServiceName={linkedServiceName}
        moduleName="Scheduled Assignments"
        handleBackClick={() => navigate('/manage/scheduled-assignments')}
      />
      <WizardNavigation currentStep={step} onStepChange={handleStepChange} />
      {step === 'details-courses' && (
        <form.Subscribe
          selector={(state) => ({
            values: state.values,
            fieldMeta: state.fieldMeta,
          })}
        >
          {({ values, fieldMeta }) => {
            let canContinue = Boolean(
              values.name &&
              fieldMeta.name?.isValid &&
              !fieldMeta.name?.errors?.length &&
              values.linkedServiceID &&
              values.goLiveDate &&
              fieldMeta.goLiveDate?.isValid &&
              !fieldMeta.goLiveDate?.errors?.length &&
              values.dueDate &&
              fieldMeta.dueDate?.isValid &&
              !fieldMeta.dueDate?.errors?.length &&
              values.courseIDs?.length > 0 &&
              fieldMeta.courseIDs?.isValid &&
              !fieldMeta.courseIDs?.errors?.length,
            )

            return (
              <ScheduledAssignmentDetailsCoursesStep
                values={{} as any}
                errors={{}}
                courses={courses}
                trainingSeries={trainingSeries}
                learningPaths={learningPaths}
                form={form}
                onCancel={handleCancel}
                onContinue={() => setStep('users')}
                canContinue={canContinue}
                isLoadingCourses={coursesStatus === 'pending'}
              />
            )
          }}
        </form.Subscribe>
      )}
      {step === 'users' && (
        <form.Subscribe
          selector={(state) => ({
            values: state.values,
            fieldMeta: state.fieldMeta,
          })}
        >
          {({ values, fieldMeta }) => {
            let canContinue = Boolean(
              values.userIDs?.length > 0 &&
              fieldMeta.userIDs?.isValid &&
              !fieldMeta.userIDs?.errors?.length,
            )

            return (
              <UserSelectionStep
                values={values}
                errors={{}}
                users={lightweightUsers}
                groups={groups as any}
                onValueChange={(field, value) => {
                  form.setFieldValue(field as any, value)
                }}
                onCancel={handleCancel}
                onBack={() => setStep('details-courses')}
                onContinue={() => setStep('review')}
                canContinue={canContinue}
              />
            )
          }}
        </form.Subscribe>
      )}
      {step === 'review' && (
        <form.Subscribe
          selector={(state) => ({
            values: state.values,
            fieldMeta: state.fieldMeta,
          })}
        >
          {({ values }) => (
            <ScheduledAssignmentReviewStep
              formData={values}
              onCancel={handleCancel}
              onBack={() => setStep('users')}
              onSubmit={() => form.handleSubmit()}
              onShowUsersSummary={() => {
                setSummaryModalType('users')
                setSummaryModalOpen(true)
              }}
              onShowCoursesSummary={() => {
                setSummaryModalType('courses')
                setSummaryModalOpen(true)
              }}
              isSubmitting={isSubmitting}
              isEditMode={true}
              users={lightweightUsers}
              courses={courses}
            />
          )}
        </form.Subscribe>
      )}
      <ConfirmationModal
        isOpen={cancelDialogOpen}
        title="Cancel Editing"
        onConfirm={handleCancelConfirm}
        onClose={() => setCancelDialogOpen(false)}
      >
        Are you sure you want to cancel? All changes will be lost.
      </ConfirmationModal>
      <SummaryModal
        isOpen={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        type={summaryModalType}
        users={lightweightUsers}
        courses={courses}
        selectedUserIDs={form.state.values.userIDs}
        selectedCourseIDs={form.state.values.courseIDs}
      />
    </Container>
  )
}
